import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Minus, Plus, ShoppingBag, ArrowLeft } from "lucide-react";
import { fetchShopProduct, absoluteShopUrl } from "../shop/api";
import { useCart } from "../shop/CartContext";
import { useSEO } from "../lib/seo";
import { toast } from "sonner";

export default function ShopProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const cart = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setLoading(true);
    fetchShopProduct(slug)
      .then((d) => {
        setProduct(d);
        setActiveImg(0);
        setQty(1);
      })
      .catch(() => toast.error("Product not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  useSEO({
    title: product ? `${product.name} — GenieX Shop` : "GenieX Shop",
    description: product?.description || "Shop GenieX smart home devices.",
    path: `/shop/${slug}`,
  });

  if (loading) {
    return (
      <main className="min-h-screen bg-[var(--gx-bg)] pt-40 text-center text-dim" data-testid="shop-product-loading">
        Loading…
      </main>
    );
  }
  if (!product) {
    return (
      <main className="min-h-screen bg-[var(--gx-bg)] pt-40 text-center text-dim" data-testid="shop-product-not-found">
        Product not found.{" "}
        <Link to="/shop" className="text-gold underline">
          Back to Shop
        </Link>
      </main>
    );
  }

  const images = product.images?.length ? product.images : [null];

  return (
    <main data-testid="shop-product-page" className="bg-[var(--gx-bg)] text-white pt-28 lg:pt-36 pb-24">
      <div className="max-w-[1300px] mx-auto px-5 lg:px-10">
        <button onClick={() => navigate(-1)} data-testid="shop-product-back" className="inline-flex items-center gap-2 text-mute hover:text-gold text-[13px] mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          <div>
            <div className="rounded-3xl overflow-hidden bg-[var(--gx-surface-2)] aspect-square">
              {images[activeImg] ? (
                <img
                  src={absoluteShopUrl(images[activeImg])}
                  alt={product.name}
                  className="w-full h-full object-contain p-6"
                  data-testid="shop-product-main-image"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-mute">
                  <ShoppingBag className="w-12 h-12" />
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-3 mt-4">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    data-testid={`shop-product-thumb-${i}`}
                    className={`w-16 h-16 rounded-xl overflow-hidden border ${activeImg === i ? "border-gold" : "border-line"}`}
                  >
                    {img && <img src={absoluteShopUrl(img)} alt="" className="w-full h-full object-cover" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="eyebrow mb-3">{product.category}</p>
            <h1 className="h2-display" data-testid="shop-product-name">
              {product.name}
            </h1>
            <div className="flex items-center gap-3 mt-5">
              <span className="text-gold font-display text-3xl" data-testid="shop-product-price">
                ₹{Number(product.price).toLocaleString("en-IN")}
              </span>
              {product.compare_at_price ? (
                <span className="text-mute text-lg line-through">₹{Number(product.compare_at_price).toLocaleString("en-IN")}</span>
              ) : null}
            </div>
            <p className="lead mt-6 max-w-[52ch]" data-testid="shop-product-description">
              {product.description}
            </p>

            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center border border-line rounded-full overflow-hidden">
                <button
                  type="button"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  data-testid="shop-qty-decrease"
                  className="w-10 h-10 flex items-center justify-center text-dim hover:text-gold"
                >
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <span className="w-10 text-center text-sm" data-testid="shop-qty-value">
                  {qty}
                </span>
                <button
                  type="button"
                  onClick={() => setQty((q) => Math.min(product.stock || 50, q + 1))}
                  data-testid="shop-qty-increase"
                  className="w-10 h-10 flex items-center justify-center text-dim hover:text-gold"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
              <span className="text-[12px] text-mute" data-testid="shop-product-stock">
                {product.stock > 0 ? `${product.stock} in stock` : "Out of stock"}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <button
                type="button"
                disabled={product.stock <= 0}
                onClick={() => {
                  cart.addItem(product, qty);
                  toast.success(`${product.name} added to cart`);
                }}
                data-testid="shop-product-add-to-cart"
                className="btn-outline flex-1 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Add to Cart
              </button>
              <button
                type="button"
                disabled={product.stock <= 0}
                onClick={() => {
                  cart.addItem(product, qty);
                  navigate("/checkout");
                }}
                data-testid="shop-product-buy-now"
                className="btn-gold flex-1 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Buy Now
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
